// Home page sections whose content comes from Admin → Customize.
import React, { useEffect, useState } from 'react';
import { CheckCircle2, Star, ArrowRight } from 'lucide-react';
import SmartLink from '../../components/SmartLink/SmartLink';
import { contentService } from '../../services/contentService';

const initialsOf = name => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

// Replace "{code}" in admin text with the highlighted coupon code.
const withCode = (text, code) => {
  if (!text) return null;
  const parts = text.split('{code}');
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && <strong>{code}</strong>}
    </React.Fragment>
  ));
};

const TwoToneTitle = ({ light, bold, className, id }) => (
  <h2 id={id} className={className}>
    {light && <span>{light}</span>} {bold && <strong>{bold}</strong>}
  </h2>
);

/* ---------------- Hero carousel ---------------- */
export const HeroSection = ({ content, loading }) => {
  const [active, setActive] = useState(0);
  const slides = content?.slides || [];
  const intervalMs = Math.max(2, content?.autoplaySeconds || 4.5) * 1000;

  useEffect(() => {
    if (active >= slides.length) setActive(0);
  }, [slides.length, active]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const timer = window.setInterval(() => setActive(current => (current + 1) % slides.length), intervalMs);
    return () => window.clearInterval(timer);
  }, [slides.length, intervalMs]);

  // Keep the space reserved while loading so the page doesn't jump
  if (loading) return <section className="hero-section" aria-busy="true" aria-label="Featured Collections" />;
  if (!content || slides.length === 0) return null;

  return (
    <section className="hero-section" aria-label="Featured Collections">
      {slides.map((slide, index) => (
        <SmartLink
          key={slide.id}
          to={slide.link}
          className={`hero-slide ${index === active ? 'active' : ''}`}
          aria-hidden={index !== active}
          tabIndex={index === active ? 0 : -1}
        >
          <picture>
            {slide.mobileImage && <source media="(max-width: 768px)" srcSet={slide.mobileImage} />}
            <img
              src={slide.image}
              alt={slide.alt || ''}
              className="hero-img"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </picture>
        </SmartLink>
      ))}

      {slides.length > 1 && (
        <div className="hero-carousel-dots" aria-label="Hero carousel controls">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className={`hero-dot ${index === active ? 'active' : ''}`}
              aria-label={`Show slide ${index + 1}`}
              onClick={(e) => {
                e.preventDefault();
                setActive(index);
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
};

/* ---------------- Special deals ---------------- */
// Card shape from Admin → Customize → Special Deals. Every shape except "ribbon" is centred like a box.
const DEAL_SHAPES = ['box', 'ribbon', 'arch', 'circle', 'leaf', 'ticket', 'hexagon', 'tag', 'slant'];
const dealShapeClass = shape => {
  const s = DEAL_SHAPES.includes(shape) ? shape : 'box';
  return s === 'ribbon' ? 'shape-ribbon' : `shape-boxlike shape-${s}`;
};

export const SpecialDealsSection = ({ content }) => {
  if (!content || !content.cards?.length) return null;
  return (
    <section className="special-deals-section" aria-labelledby="special-deals-title">
      <div className="home-container">
        <TwoToneTitle id="special-deals-title" className="special-deals-title" light={content.titleLight} bold={content.titleBold} />

        <div className={`special-deals-grid deals-count-${Math.min(content.cards.length, 4)} ${dealShapeClass(content.cardShape)}`}>
          {content.cards.map(card => {
            const gradient = `linear-gradient(145deg, ${card.bgColor} 0%, ${card.bgColor2 || card.bgColor} 100%)`;
            const style = {
              '--deal-bg': card.image
                ? `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.45) 100%), url("${card.image}") center / cover no-repeat`
                : gradient,
              '--deal-text': card.textColor,
              '--deal-shadow': card.shadowColor
            };
            return (
              <SmartLink key={card.id} to={card.link} className="special-deal-card" style={style}>
                <span className="special-deal-label">{card.label}</span>
                {card.prefix && <span className="special-deal-under">{card.prefix}</span>}
                {card.price && <span className="special-deal-price">{card.price}</span>}
              </SmartLink>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Customer reviews ---------------- */
export const ReviewsSection = ({ content }) => {
  if (!content || !content.items?.length) return null;
  return (
    <section className="home-section reviews-section" aria-label="Customer Testimonials">
      <div className="home-container">
        <div className="section-header text-center">
          {content.kicker && <span className="section-kicker">{content.kicker}</span>}
          <TwoToneTitle className="section-title" light={content.titleLight} bold={content.titleBold} />
          {content.subtitle && <p className="section-subtitle">{content.subtitle}</p>}
        </div>

        <div className={`reviews-grid reviews-count-${Math.min(content.items.length, 3)}`}>
          {content.items.map(review => (
            <div key={review.id} className={`review-card ${review.badge ? 'featured-review' : ''}`}>
              {review.badge && <div className="review-badge">{review.badge}</div>}
              <div className="review-stars" aria-label={`${review.rating} out of 5 stars`}>
                {[1, 2, 3, 4, 5].map(n => (
                  <Star
                    key={n}
                    size={15}
                    fill={n <= review.rating ? '#C49752' : 'none'}
                    color="#C49752"
                  />
                ))}
              </div>
              <p className="review-body">"{review.text}"</p>
              <div className="reviewer-info">
                {review.photo ? (
                  <img src={review.photo} alt={review.name} className="reviewer-avatar reviewer-photo" loading="lazy" />
                ) : (
                  <div className="reviewer-avatar">{initialsOf(review.name)}</div>
                )}
                <div>
                  <p className="reviewer-name">{review.name}</p>
                  <span className="reviewer-location">
                    {[review.location, review.verified ? 'Verified Buyer' : ''].filter(Boolean).join(' • ')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Brand story ---------------- */
export const StorySection = ({ content }) => {
  if (!content) return null;
  return (
    <section className="home-section story-section" aria-label="Brand Craft Story">
      <div className="home-container">
        <div className={`story-card ${content.imagePosition === 'left' ? 'story-card--image-left' : ''}`}>
          <div className="story-text-col">
            {content.kicker && <span className="section-kicker">{content.kicker}</span>}
            <h2 className="story-title">{content.title}</h2>
            {content.text && <p className="story-p">{content.text}</p>}
            {content.pillars?.length > 0 && (
              <div className={`story-pillars pillars-count-${content.pillars.length}`}>
                {content.pillars.map((pillar, index) => (
                  <div key={pillar.id} className="pillar">
                    <span className="pillar-num">{String(index + 1).padStart(2, '0')}</span>
                    <h3>{pillar.title}</h3>
                    {pillar.text && <p>{pillar.text}</p>}
                  </div>
                ))}
              </div>
            )}
            {content.buttonText && content.buttonLink && (
              <SmartLink to={content.buttonLink} className="story-btn">
                <span>{content.buttonText}</span>
                <ArrowRight size={16} />
              </SmartLink>
            )}
          </div>
          <div className="story-image-col">
            <img src={content.image} alt={content.imageAlt || ''} className="story-img" loading="lazy" />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Community gallery ---------------- */
export const CommunitySection = ({ content }) => {
  if (!content || !content.items?.length) return null;
  return (
    <section className="home-section community-section" aria-label="Community Gallery">
      <div className="home-container">
        <div className="section-header text-center">
          {content.kicker && <span className="section-kicker">{content.kicker}</span>}
          <TwoToneTitle className="section-title" light={content.titleLight} bold={content.titleBold} />
          {content.subtitle && <p className="section-subtitle">{content.subtitle}</p>}
        </div>

        <div className="ugc-gallery-grid">
          {content.items.map(item => (
            <SmartLink key={item.id} to={item.link} className="ugc-item">
              <img
                src={item.image}
                alt={item.caption ? `Customer styling ${item.caption}` : 'Customer photo'}
                className="ugc-img"
                loading="lazy"
              />
              {(item.handle || item.caption) && (
                <div className="ugc-overlay">
                  {item.handle && <span className="ugc-tag">{item.handle}</span>}
                  {item.caption && <span className="ugc-product">{item.caption}</span>}
                </div>
              )}
            </SmartLink>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Welcome offer / newsletter ---------------- */
export const WelcomeSection = ({ content }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done
  const [error, setError] = useState('');

  if (!content) return null;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('sending');
    setError('');
    try {
      await contentService.subscribeNewsletter(email.trim());
      setStatus('done');
      setEmail('');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  const style = {
    '--welcome-bg': `linear-gradient(135deg, ${content.bgColor} 0%, ${content.bgColor2 || content.bgColor} 100%)`,
    '--welcome-accent': content.accentColor,
    '--welcome-button': content.buttonColor
  };

  return (
    <section className="welcome-club-section" aria-label="First Order Privilege">
      <div className="home-container">
        <div className="welcome-club-box" style={style}>
          <div className="welcome-club-content">
            {content.badge && <span className="welcome-club-badge">{content.badge}</span>}
            <h2 className="welcome-club-title">{content.title}</h2>
            {content.text && <p className="welcome-club-text">{withCode(content.text, content.couponCode)}</p>}
          </div>

          <form className="welcome-club-form" onSubmit={handleSubscribe}>
            {status === 'done' ? (
              <div className="subscribed-msg" role="status">
                <CheckCircle2 size={20} color="#2D6A4F" />
                <span>{withCode(content.successMessage || 'Thank you for subscribing!', content.couponCode)}</span>
              </div>
            ) : (
              <>
                <div className="email-input-group">
                  <input
                    type="email"
                    placeholder={content.placeholder || 'Enter your email address...'}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    required
                    className="welcome-email-input"
                    aria-label="Email address"
                  />
                  <button type="submit" className="welcome-submit-btn" disabled={status === 'sending'}>
                    <span>{status === 'sending' ? 'Please wait…' : content.buttonText}</span>
                  </button>
                </div>
                {error && <p className="welcome-error" role="alert">{error}</p>}
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};
